/**
 * user-portal-users-list.ts
 *
 * Handles the AJAX behaviour for the Org Portal → User Portal Users list page:
 * - 300ms debounce on the search input (#searchTerm)
 * - Immediate fetch on the status select change
 * - Cancels in-flight requests using AbortController when a new input arrives
 * - Updates #userPortalUsersList innerHTML with the returned partial view HTML
 * - Keeps the browser URL in sync with window.history.replaceState
 * - Toggles the clear-filters button (#clearFiltersContainer) based on active filters
 *
 * Mirrors the pattern established in org-users-list.ts.
 */

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let activeController: AbortController | null = null;

/**
 * Fetches the user portal users list partial from the server and replaces the
 * #userPortalUsersList div content.
 */
async function fetchUserPortalUsersList(url: string): Promise<void> {
  if (activeController) {
    activeController.abort();
  }

  activeController = new AbortController();

  const listEl = document.getElementById('userPortalUsersList');
  if (!listEl) return;

  // Signal that a list refresh is in progress (consumed by user-list-export.ts)
  listEl.dispatchEvent(new CustomEvent('list-refresh', { detail: { loading: true } }));

  try {
    const response = await fetch(url, {
      signal: activeController.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) {
      console.error(`User portal users list fetch failed: ${response.status}`);
      return;
    }

    const html = await response.text();
    listEl.innerHTML = html;

    window.history.replaceState(null, '', url);
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return;
    }
    console.error('Unexpected error loading user portal users list:', err);
  } finally {
    listEl.dispatchEvent(new CustomEvent('list-refresh', { detail: { loading: false } }));
  }
}

/**
 * Builds a URL for the user portal users list with the current filter values.
 * Always resets pageNumber to 1 when a filter changes.
 */
function buildListUrl(): string {
  const listEl = document.getElementById('userPortalUsersList');
  const baseUrl = listEl?.dataset['listUrl'] ?? window.location.pathname;

  const searchInput = document.getElementById('searchTerm') as HTMLInputElement | null;
  const statusSelect = document.getElementById('statusFilter') as HTMLSelectElement | null;

  const params = new URLSearchParams();
  const search = searchInput?.value.trim() ?? '';
  const status = statusSelect?.value ?? '';

  if (search) params.set('searchTerm', search);
  if (status) params.set('status', status);

  params.set('pageNumber', '1');

  return `${baseUrl}?${params.toString()}`;
}

function hasActiveFilters(): boolean {
  const searchInput = document.getElementById('searchTerm') as HTMLInputElement | null;
  const statusSelect = document.getElementById('statusFilter') as HTMLSelectElement | null;
  return (searchInput?.value.trim() ?? '') !== '' || (statusSelect?.value ?? '') !== '';
}

function updateClearButton(hasFilters: boolean): void {
  const container = document.getElementById('clearFiltersContainer');
  if (!container) return;
  container.classList.toggle('d-none', !hasFilters);
}

/**
 * Initialises the user portal users list AJAX behaviour.
 * Call once after the DOM is ready.
 * No-op when the required #userPortalUsersList element is absent from the page.
 */
export function initUserPortalUsersList(): void {
  const listEl = document.getElementById('userPortalUsersList');
  if (!listEl) return;

  const searchInput = document.getElementById('searchTerm') as HTMLInputElement | null;
  const statusSelect = document.getElementById('statusFilter') as HTMLSelectElement | null;

  if (searchInput) {
    searchInput.addEventListener('input', () => {
      if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        const url = buildListUrl();
        updateClearButton(hasActiveFilters());
        void fetchUserPortalUsersList(url);
      }, 300);
    });
  }

  if (statusSelect) {
    statusSelect.addEventListener('change', () => {
      updateClearButton(hasActiveFilters());
      void fetchUserPortalUsersList(buildListUrl());
    });
  }

}

